import { EVIDENCE_IFU_AU } from '@/components/utils/legalDocuments';
import { useEffectiveEvidenceLocation } from '@/hooks/useEffectiveEvidenceLocation';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { countriesToRegion } from '@/lists/countriesToRegion';
import { Region } from '@heidi/schema/ml-scribe';
import { useQueryUser } from '@heidi/workspace/data';

/**
 * The Instructions For Use document shipped for each Evidence region. The
 * single place a region is paired with a document: a region absent here has
 * no IFU, and every surface that links one reads the pairing from this map.
 */
const EVIDENCE_IFU_DOCUMENTS: Readonly<Partial<Record<Region, string>>> = {
  [Region.AU]: EVIDENCE_IFU_AU,
};

/**
 * Href of the Instructions For Use for the signed-in user's effective Evidence
 * region, or `null` when that region ships none. The IFU follows the stored
 * Evidence country, else the account country (TGA compliance for users who
 * have not set `evidence_settings` yet).
 */
export function useEvidenceIfuHref(): string | null {
  const { isSessionlessEvidenceShown } = useEvidenceAccess();
  const { country: evidenceCountry } = useEffectiveEvidenceLocation();
  const { data: accountCountry } = useQueryUser((data) => data?.country ?? null);

  if (!isSessionlessEvidenceShown) return null;

  const effectiveCountry = evidenceCountry ?? accountCountry ?? null;
  if (!effectiveCountry) return null;

  const region = countriesToRegion[effectiveCountry];
  return region != null ? (EVIDENCE_IFU_DOCUMENTS[region] ?? null) : null;
}

export function useIsEvidenceIfuVisible(): boolean {
  return useEvidenceIfuHref() !== null;
}
